import { IgButton } from "@intelligems/ig-theme";

import { RuleExperiencesDialog } from "../rules/RuleExperiencesDialog";
import type { RuleListUsage } from "../rules/ruleListRows";
import type { RuleSaveAction } from "./publishOnSave";

interface SharedRuleSaveDialogProps {
  open: boolean;
  experiences: RuleListUsage[];
  /**
   * What the Save behind this dialog actually does — the same value the page
   * hands `saveRule` (`publishOnSave.ts`). Required rather than defaulted: the
   * dialog's whole job is to state the consequence, and a default would let a
   * caller that never resolved one state the wrong consequence silently.
   */
  saveAction: RuleSaveAction;
  isSaving: boolean;
  onClose: () => void;
  onSaveAsNewRule: () => void;
  onSaveToAll: () => void;
}

/**
 * The consent gate before a rule that several experiences share is saved.
 *
 * It says what Save is about to do, and that is now two different things. A
 * rule nobody is serving still persists AND deploys in one act, so the change
 * really does land in every experience below. A rule that IS serving — which,
 * this dialog being raised on multi-experience rules, is the common case here —
 * writes its draft and stops (`publishOnSave.ts`): the experiences below keep
 * showing the published version until the merchant publishes.
 *
 * Both halves turn on that, the sentence and the verb. A dialog that promised
 * "Saving updates the products shown in every experience below" over a draft
 * write would be the same control-lies-about-what-it-does defect the split of
 * Save was raised to fix, moved one layer up — and worse here, because the
 * banner that does tell the truth is behind the dialog while it is open.
 */
export const SharedRuleSaveDialog = ({
  open,
  experiences,
  saveAction,
  isSaving,
  onClose,
  onSaveAsNewRule,
  onSaveToAll,
}: SharedRuleSaveDialogProps) => {
  const experienceCount = experiences.length;
  const deploys = saveAction === "save_and_publish";

  return (
    <RuleExperiencesDialog
      open={open}
      title={`This rule is used in ${experienceCount} experiences`}
      description={
        deploys
          ? "Saving updates the products shown in every experience below. If the change should only apply in one place, save it as a new rule instead."
          : "Saving records the change without publishing it — the experiences below keep serving the published version until you publish. If the change should only apply in one place, save it as a new rule instead."
      }
      experiences={experiences}
      onClose={onClose}
      actions={
        <>
          <IgButton
            color="secondary"
            disabled={isSaving}
            onClick={onSaveAsNewRule}
          >
            Save as a new rule
          </IgButton>
          <IgButton loading={isSaving} onClick={onSaveToAll}>
            {deploys
              ? `Save to all ${experienceCount} experiences`
              : "Save draft"}
          </IgButton>
        </>
      }
    />
  );
};
